import * as React from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { LoadingButton } from '@mui/lab';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { toast } from '@/components/core/v2/toaster';

import {
  createCommunityCustomField,
  deleteCommunityCustomField,
  getCommunityCustomFields,
  type CommunityCustomField,
  updateCommunityCustomField,
  updateCommunityPreferences,
} from '@/api/communities';
import { useUser } from '@/hooks/v2/use-user';

const DEFAULT_RESERVATION_DESCRIPTION =
  'View all amenity reservations. Approved reservations appear in blue; your pending requests remain visible to you in orange while waiting for approval. Use the "New Reservation" button to book an amenity.';

export default function CommunityPreferencesPage(): React.JSX.Element | null {
  const { community, setCommunity } = useUser();
  const queryClient = useQueryClient();

  const { data: customFieldsData, isLoading: customFieldsLoading } = useQuery({
    queryKey: ['community', community?.id, 'custom-fields'],
    queryFn: () => getCommunityCustomFields(community!.id),
    enabled: !!community?.id,
  });
  const customFields = customFieldsData?.data ?? [];

  const createFieldMutation = useMutation({
    mutationFn: (data: { name: string; visible_to_member: boolean; editable_by_member: boolean }) =>
      createCommunityCustomField(community!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community', community?.id, 'custom-fields'] });
      toast.success('Field added');
      addFieldReset({ name: '', visible_to_member: true, editable_by_member: true });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Failed to add field');
    },
  });

  const deleteFieldMutation = useMutation({
    mutationFn: (customFieldId: string) =>
      deleteCommunityCustomField(community!.id, customFieldId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community', community?.id, 'custom-fields'] });
      toast.success('Field removed');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Failed to remove field');
    },
  });

  const [editingFieldId, setEditingFieldId] = React.useState<string | null>(null);
  const [editName, setEditName] = React.useState('');
  const [editVisible, setEditVisible] = React.useState(true);
  const [editEditable, setEditEditable] = React.useState(true);

  const updateFieldMutation = useMutation({
    mutationFn: (payload: { id: string; name: string; visible_to_member: boolean; editable_by_member: boolean }) =>
      updateCommunityCustomField(community!.id, payload.id, {
        name: payload.name.trim(),
        visible_to_member: payload.visible_to_member,
        editable_by_member: payload.editable_by_member,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['community', community?.id, 'custom-fields'] });
      toast.success(`Saved: ${variables.name}`);
      setEditingFieldId(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Failed to save field');
    },
  });

  const startEditing = (field: CommunityCustomField) => {
    setEditingFieldId(field.id);
    setEditName(field.name);
    setEditVisible(field.visible_to_member);
    setEditEditable(field.editable_by_member);
  };

  const cancelEditing = () => {
    setEditingFieldId(null);
  };

  const {
    control: addFieldControl,
    handleSubmit: addFieldSubmit,
    reset: addFieldReset,
    formState: { isSubmitting: addFieldSubmitting },
  } = useForm<{ name: string; visible_to_member: boolean; editable_by_member: boolean }>({
    defaultValues: { name: '', visible_to_member: true, editable_by_member: true },
  });

  const preferencesMutation = useMutation({
    mutationFn: (data: { amenity_reservations_description?: string | null; discussion_page_enabled?: boolean }) =>
      updateCommunityPreferences(community!.id, data),
    onSuccess: (updatedCommunity) => {
      setCommunity({ ...community!, ...updatedCommunity }, false);
      queryClient.invalidateQueries({ queryKey: ['community', community?.id] });
      toast.success('Saved');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Failed to save');
    },
  });

  const reservationForm = useForm<{ amenity_reservations_description: string }>({
    defaultValues: { amenity_reservations_description: community?.amenity_reservations_description ?? '' },
  });
  React.useEffect(() => {
    reservationForm.reset({ amenity_reservations_description: community?.amenity_reservations_description ?? '' });
  }, [community?.amenity_reservations_description, reservationForm.reset]);

  const pagesForm = useForm<{ discussion_page_enabled: boolean }>({
    defaultValues: { discussion_page_enabled: community?.discussion_page_enabled ?? true },
  });
  React.useEffect(() => {
    pagesForm.reset({ discussion_page_enabled: community?.discussion_page_enabled ?? true });
  }, [community?.discussion_page_enabled, pagesForm.reset]);

  if (!community) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 720 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          Configure how this community appears to members: reservation page copy, which pages are visible in the sidebar, and other preferences. Changes apply to all members of this community.
        </Typography>
      </Paper>
      <Stack spacing={3}>
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Reservation page description</Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <form
              onSubmit={reservationForm.handleSubmit((data) =>
                preferencesMutation.mutate({
                  amenity_reservations_description: data.amenity_reservations_description?.trim() || null,
                })
              )}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This text appears at the top of the amenity reservations page for residents. Leave blank to use the default message.
              </Typography>
              <Controller
                name="amenity_reservations_description"
                control={reservationForm.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={4}
                    placeholder={DEFAULT_RESERVATION_DESCRIPTION}
                    label="Description"
                    helperText="Shown on the reservations page (Reserve an Amenity)."
                    sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
              />
              <LoadingButton
                type="submit"
                variant="contained"
                loading={reservationForm.formState.isSubmitting || preferencesMutation.isPending}
              >
                Save
              </LoadingButton>
            </form>
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Pages</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Choose which pages are visible to community members in the main navigation.</Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <form
              onSubmit={pagesForm.handleSubmit((data) =>
                preferencesMutation.mutate({ discussion_page_enabled: data.discussion_page_enabled })
              )}
            >
              <Stack spacing={2}>
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Discussion
                    </Typography>
                    <Controller
                      name="discussion_page_enabled"
                      control={pagesForm.control}
                      render={({ field: { value, onChange, ...field } }) => (
                        <Switch
                          {...field}
                          checked={value ?? true}
                          onChange={(e) => onChange(e.target.checked)}
                        />
                      )}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    A shared board where members can start topics and reply to each other—useful for announcements, questions, and community conversation.
                  </Typography>
                </Box>
                {/* Add more page toggles here as needed, e.g. Documents, Announcements, etc. */}
              </Stack>
              <LoadingButton
                type="submit"
                variant="contained"
                sx={{ mt: 2 }}
                loading={pagesForm.formState.isSubmitting || preferencesMutation.isPending}
              >
                Save
              </LoadingButton>
            </form>
          </Box>
        </Paper>
      </Stack>

      <Stack spacing={3} sx={{ mt: 3 }}>
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Resident profile fields</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Extra fields shown on resident profiles (e.g. emergency contact, parking spot). Members can see and edit only fields you allow.</Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Visible to member: residents can see this field on their profile. Editable by member: residents can update it themselves when things change.
            </Typography>
            {customFieldsLoading ? (
              <Typography color="text.secondary">Loading fields…</Typography>
            ) : (
              <Stack spacing={2}>
                {customFields.map((field: CommunityCustomField) => (
                  <Box
                    key={field.id}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    {editingFieldId === field.id ? (
                      <>
                        <TextField
                          size="small"
                          fullWidth
                          label="Field name"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          sx={{ maxWidth: 360 }}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={editVisible}
                                onChange={(e) => setEditVisible(e.target.checked)}
                              />
                            }
                            label="Visible to member"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={editEditable}
                                onChange={(e) => setEditEditable(e.target.checked)}
                              />
                            }
                            label="Editable by member"
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <LoadingButton
                            size="small"
                            variant="contained"
                            loading={updateFieldMutation.isPending}
                            onClick={() =>
                              updateFieldMutation.mutate({
                                id: field.id,
                                name: editName,
                                visible_to_member: editVisible,
                                editable_by_member: editEditable,
                              })
                            }
                            disabled={!editName.trim()}
                          >
                            Save
                          </LoadingButton>
                          <LoadingButton
                            size="small"
                            variant="outlined"
                            onClick={cancelEditing}
                            disabled={updateFieldMutation.isPending}
                          >
                            Cancel
                          </LoadingButton>
                        </Box>
                      </>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: 1,
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {field.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Visible to member: {field.visible_to_member ? 'Yes' : 'No'} · Editable by member: {field.editable_by_member ? 'Yes' : 'No'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            aria-label="Edit field"
                            onClick={() => startEditing(field)}
                          >
                            <EditOutlined fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="Remove field"
                            onClick={() => deleteFieldMutation.mutate(field.id)}
                            disabled={deleteFieldMutation.isPending}
                          >
                            <DeleteOutlined fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                  </Box>
                ))}
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Add a field
                  </Typography>
                  <form
                    onSubmit={addFieldSubmit((data) =>
                      createFieldMutation.mutate({
                        name: data.name.trim(),
                        visible_to_member: data.visible_to_member,
                        editable_by_member: data.editable_by_member,
                      })
                    )}
                  >
                    <Stack spacing={2}>
                      <Controller
                        name="name"
                        control={addFieldControl}
                        rules={{ required: 'Name is required', maxLength: { value: 255, message: 'Max 255 characters' } }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            size="small"
                            fullWidth
                            label="Field name"
                            placeholder="e.g. Emergency contact"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Controller
                          name="visible_to_member"
                          control={addFieldControl}
                          render={({ field: { value, onChange, ...f } }) => (
                            <FormControlLabel
                              control={<Checkbox checked={value} onChange={(e) => onChange(e.target.checked)} {...f} />}
                              label="Visible to member"
                            />
                          )}
                        />
                        <Controller
                          name="editable_by_member"
                          control={addFieldControl}
                          render={({ field: { value, onChange, ...f } }) => (
                            <FormControlLabel
                              control={<Checkbox checked={value} onChange={(e) => onChange(e.target.checked)} {...f} />}
                              label="Editable by member"
                            />
                          )}
                        />
                      </Box>
                      <LoadingButton
                        type="submit"
                        variant="outlined"
                        size="small"
                        loading={addFieldSubmitting || createFieldMutation.isPending}
                      >
                        Add field
                      </LoadingButton>
                    </Stack>
                  </form>
                </Box>
              </Stack>
            )}
          </Box>
        </Paper>
      </Stack>

    </Box>
  );
}
